"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.decryptNeo2 = exports.decrypt = exports.encrypt = void 0;
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * NEP2 - Private Key Encryption based on AES.
 * This encrypts your private key with a passphrase, protecting your private key from being stolen and used.
 * It is useful for storing private keys in a JSON file securely or to mask the key before printing it.
 */
const aes_1 = __importDefault(require("crypto-js/aes"));
const enc_hex_1 = __importDefault(require("crypto-js/enc-hex"));
const enc_latin1_1 = __importDefault(require("crypto-js/enc-latin1"));
const mode_ecb_1 = __importDefault(require("crypto-js/mode-ecb"));
const pad_nopadding_1 = __importDefault(require("crypto-js/pad-nopadding"));
const sha256_1 = __importDefault(require("crypto-js/sha256"));
const crypto_js_1 = require("crypto-js");
const scrypt_js_1 = require("scrypt-js");
const consts_1 = require("../consts");
const logging_1 = __importDefault(require("../logging"));
const u_1 = require("../u");
const verify_1 = require("./verify");
const core_1 = require("./core");
const bs58_1 = __importDefault(require("bs58"));
const buffer_1 = require("buffer");
const NEO2_ADDRESS_VERSION = "17";
const enc = {
    Latin1: enc_latin1_1.default,
    Hex: enc_hex_1.default,
};
const AES_OPTIONS = { mode: mode_ecb_1.default, padding: pad_nopadding_1.default };
const log = (0, logging_1.default)("wallet");
async function createNep2Key(prefix, privateKey, keyphrase, address, scryptParams) {
    const { n, r, p } = scryptParams;
    // SHA Salt (use the first 4 bytes)
    const firstSha = (0, sha256_1.default)(enc.Latin1.parse(address));
    const addressHash = (0, sha256_1.default)(firstSha)
        .toString()
        .slice(0, 8);
    const key = await (0, scrypt_js_1.scrypt)(buffer_1.Buffer.from(keyphrase.normalize("NFC"), "utf8"), buffer_1.Buffer.from(addressHash, "hex"), n, r, p, 64, () => { } // eslint-disable-line
    );
    const derived = buffer_1.Buffer.from(key).toString("hex");
    const derived1 = derived.slice(0, 64);
    const derived2 = derived.slice(64);
    // AES Encrypt
    const xor = (0, u_1.hexXor)(privateKey, derived1);
    const encrypted = aes_1.default.encrypt(enc.Hex.parse(xor), enc.Hex.parse(derived2), AES_OPTIONS);
    const assembled = consts_1.NEP2_HEADER + consts_1.NEP2_FLAG + addressHash + encrypted.ciphertext.toString();
    const checksum = (0, u_1.hash256)(assembled).substr(0, 8);
    const encryptedKey = bs58_1.default.encode((0, u_1.hexstring2ab)(assembled + checksum));
    log.info(`Successfully encrypted key to ${encryptedKey}`);
    return encryptedKey;
}
function getAddressFromPrivateKey(privateKey, addressVersion) {
    return (0, core_1.getAddressFromScriptHash)((0, core_1.getScriptHashFromPublicKey)((0, core_1.getPublicKeyFromPrivateKey)(privateKey)), addressVersion);
}
function getNeo2AddressFromPrivateKey(privateKey) {
    const publicKey = (0, core_1.getPublicKeyFromPrivateKey)(privateKey, true);
    const verificationScript = "21" + publicKey + "ac";
    const scriptHash = (0, u_1.hash160)(verificationScript);
    const shaChecksum = (0, u_1.hash256)(NEO2_ADDRESS_VERSION + scriptHash).substr(0, 8);
    return bs58_1.default.encode(buffer_1.Buffer.from(NEO2_ADDRESS_VERSION + scriptHash + shaChecksum, "hex"));
}
/**
 * Encrypts a WIF key using a given keyphrase under NEP-2 Standard.
 * @param wifKey - WIF key to encrypt (52 chars long).
 * @param keyphrase - password will be encoded as UTF-8 and normalized using Unicode Normalization Form C (NFC).
 * @param scryptParams - optional parameters for Scrypt. Defaults to NEP2 specified parameters.
 * @returns The encrypted key in Base58 (Case sensitive).
 */
function encrypt(wifKey, keyphrase, scryptParams = consts_1.DEFAULT_SCRYPT, addressVersion = consts_1.DEFAULT_ADDRESS_VERSION) {
    const privateKey = (0, verify_1.isWIF)(wifKey) ? (0, core_1.getPrivateKeyFromWIF)(wifKey) : wifKey;
    const address = getAddressFromPrivateKey(privateKey, addressVersion);
    return createNep2Key(consts_1.NEP2_HEADER + consts_1.NEP2_FLAG, privateKey, keyphrase, address, scryptParams);
}
exports.encrypt = encrypt;
async function decipherNep2Key(encryptedKey, keyphrase, generateAddress, scryptParams) {
    const { n, r, p } = scryptParams;
    const assembledWithChecksum = (0, u_1.ab2hexstring)(bs58_1.default.decode(encryptedKey));
    const assembled = assembledWithChecksum.substr(0, assembledWithChecksum.length - 8);
    const checksum = assembledWithChecksum.substr(-8);
    if ((0, u_1.hash256)(assembled).substr(0, 8) !== checksum) {
        throw new Error("Base58 checksum failed.");
    }
    const addressHash = assembled.substr(6, 8);
    const encrypted = assembled.substr(-64);
    const key = await (0, scrypt_js_1.scrypt)(buffer_1.Buffer.from(keyphrase.normalize("NFC"), "utf8"), buffer_1.Buffer.from(addressHash, "hex"), n, r, p, 64, () => { } // eslint-disable-line
    );
    const derived = buffer_1.Buffer.from(key).toString("hex");
    const derived1 = derived.slice(0, 64);
    const derived2 = derived.slice(64);
    const ciphertext = crypto_js_1.lib.CipherParams.create({
        ciphertext: enc.Hex.parse(encrypted),
    });
    const decrypted = aes_1.default.decrypt(ciphertext, enc.Hex.parse(derived2), AES_OPTIONS);
    const privateKey = (0, u_1.hexXor)(decrypted.toString(), derived1);
    const address = generateAddress(privateKey);
    const newAddressHash = (0, sha256_1.default)((0, sha256_1.default)(enc.Latin1.parse(address)))
        .toString()
        .slice(0, 8);
    if (addressHash !== newAddressHash) {
        throw new Error("Wrong password or scrypt parameters!");
    }
    log.info(`Successfully decrypted ${encryptedKey}`);
    return privateKey;
}
/**
 * Decrypts an encrypted key using a given keyphrase under NEP-2 Standard.
 * @param encryptedKey - tThe encrypted key (58 chars long).
 * @param keyphrase - the password will be encoded as UTF-8 and normalized using Unicode Normalization Form C (NFC).
 * @param scryptParams - parameters for Scrypt. Defaults to NEP2 specified parameters.
 * @returns The decrypted WIF key.
 */
async function decrypt(encryptedKey, keyphrase, scryptParams = consts_1.DEFAULT_SCRYPT, addressVersion = consts_1.DEFAULT_ADDRESS_VERSION) {
    const privateKey = await decipherNep2Key(encryptedKey, keyphrase, (privateKey) => getAddressFromPrivateKey(privateKey, addressVersion), scryptParams);
    return (0, core_1.getWIFFromPrivateKey)(privateKey);
}
exports.decrypt = decrypt;
async function decryptNeo2(encryptedKey, keyphrase, scryptParams = consts_1.DEFAULT_SCRYPT) {
    const privateKey = await decipherNep2Key(encryptedKey, keyphrase, getNeo2AddressFromPrivateKey, scryptParams);
    return (0, core_1.getWIFFromPrivateKey)(privateKey);
}
exports.decryptNeo2 = decryptNeo2;
